import { Container } from 'pixi.js';
import {
  StudioAnimation,
  StudioImageShapeDescriptor,
  StudioScene,
} from '../../../../store/studio/studio.types';
import {
  _screenX,
  _screenY,
} from '../../../../../services/canvas/canvas.service';
import { StudioSceneShapeCell } from './studio-scene-shape-cell';
import {
  deleteVideoInstance,
  saveScene,
  StudioService,
  StudioVideoShapeDescriptor,
  setCurrentStudioSceneId,
  addLastStudioScene,
  StudioSceneBackgroundDefinition,
} from '../../../../store/studio';
import { cloneDeep, isEqual, omit } from 'lodash';
import gsap from 'gsap';
import {
  StudioTextShape,
  StudioTextShapeDescriptor,
} from '../../../../store/studio/text/studio-text-shape';
import { VideoShape } from '../../base/video-shape';
import { StudioImageShape } from '../../../../store/studio/shapes/image/studio-image.shape';
import { RectangleElement } from '../../primitive/rectangle-element';
import { SceneTransition } from '../../../../animation/text-animation.service';
import { TextElement } from '../../primitive/text-element';
import { PathElement } from '../../primitive/path-element';
import {
  StudioElementShape,
  StudioElementShapeDescriptor,
} from '../../../../store/studio/element/studio-element-shape';
import { RootLayoutCell } from '../../../../store/studio/layout/layout-cell';
import { AnimationContext } from '../../../../store/studio/animation/studio-animation.service';
export interface StudioSceneShapeConfig {
  margin?: number;
  preview?: boolean;
}
//
export class StudioSceneShape {
  container: Container;
  shapeCells: Record<string, StudioSceneShapeCell> = {};
  maskRectangle: RectangleElement;
  shapes: Record<
    string,
    StudioTextShape | VideoShape | StudioImageShape | StudioElementShape
  > = {};
  private _shapes: any[] = [];
  get cs() {
    return this.shapeService.cs;
  }

  get store() {
    return this.shapeService.store;
  }

  get animationService() {
    return this.shapeService.animationService;
  }

  get newCellAlignment() {
    // const margin = this.config?.margin ?? 0.1;
    const margin = this.config?.margin ?? 0;
    const mx = _screenX * margin;
    const my = _screenY * margin;

    if (this.cs.isPortrait) {
      return {
        x: mx,
        y: my,
        w: this.cs.portraitScreenX - 2 * mx,
        h: this.cs.portraitScreenY - 2 * mx,
      };
    }

    return {
      x: mx,
      y: my,
      w: this.cs._screenX - 2 * mx,
      h: this.cs._screenY - 2 * my,
    };
  }
  get index() {
    return this.scene.index;
  }

  get shapeService() {
    return this.studioService.shapeService;
  }

  get positionIndex() {
    return this.scene.positionIndex;
  }
  defaultPosition = 0;

  get orientationService() {
    return {} as any;
  }
  // general-shape methods
  getType() {
    return 'studio-scene-shape';
  }

  get groupShapeParent() {
    return null;
  }

  localStartDrag() {
    this.shapeService.startDrag(this);
  }

  circleContainer: Container;
  auxCircleContainer: Container;

  get x() {
    return this.container.position.x;
  }
  get y() {
    return this.container.position.y;
  }

  hideOrientationLines() {
    // Object.values(this.shapeCells).forEach(cell => cell.hideOrientationLines()); //
  }
  get prev() {
    return this.studioService.scenesByPositionIndex[
      this.scene.positionIndex - 1
    ] as StudioSceneShape;
  }
  get next() {
    return this.studioService.scenesByPositionIndex[
      this.scene.positionIndex + 1
    ] as StudioSceneShape;
  }
  get loadPromise() {
    // console.log('shapeCells', this.shapeCells);
    return Promise.all(
      Object.values(this.shapes).map(cell => cell.loadPromise),
    );
  }
  get service() {
    return this.studioService.shapeService;
  }

  get isAddAfterLast() {
    return this.scene.id === 'add-after-last';
  }

  layout: RootLayoutCell;
  animationByLayout: StudioAnimation;
  constructor(
    public readonly studioService: StudioService,
    public scene: StudioScene,
    private config?: StudioSceneShapeConfig,
  ) {
    try {
      if (this.scene.layoutDefinition) {
        this.layout = new RootLayoutCell(this.cs, this.scene.layoutDefinition);
        // this.animationByLayout = this.layout.getAnimation();
      }
    } catch (error) {
      console.log('error', error);
    }

    this.initContainers();
    this.initShapes();
    this.calcAnimationTime();

    if (this.isAddAfterLast) {
      this.jnitAddNewScene();
    }
  }

  jnitAddNewScene() {
    this.hide();
    // -- //

    let x: number, y: number, w: number, h: number;

    if (this.cs.isPortrait) {
      w = this.cs.currentScreenX * 0.6;
      h = this.cs.currentScreenY * 0.2;
      x = this.cs.currentScreenX * 0.2;
      y = this.cs.currentScreenY * 0.4;
    } else {
      w = this.cs.currentScreenX * 0.6;
      h = this.cs.currentScreenY * 0.2;
      x = this.cs.currentScreenX * 0.2;
      y = this.cs.currentScreenY * 0.4;
    }

    // Plus icon dimensions
    const iconSize = 32;
    const textX = x + w / 2 - 100;
    const textY = y + h / 2 - 21;
    const iconX = textX + 180;
    const iconY = y + h / 2 - iconSize / 2;

    const rectangle = new RectangleElement(this, this.container, {
      position: {
        x,
        y,
      },
      width: w,
      height: h,
      fill: '#ffffff',
      'fill-opacity': 0.001,
      r: 24,
    });

    rectangle.element.interactive = true;
    rectangle.element.cursor = 'pointer';

    this.container.alpha = 0.5;
    this.bgContainer.alpha = 0.3;
    rectangle.element.addEventListener('mouseenter', () => {
      this.container.alpha = 0.8;
    });
    rectangle.element.addEventListener('mouseleave', () => {
      this.container.alpha = 0.5;
    });

    rectangle.element.onclick = () => {
      this.store.dispatch(addLastStudioScene());
      if (this.cs.isPortrait) {
        this.container.position.x -= this.cs.sceneWithGap;
        this.bgContainer.position.x -= this.cs.sceneWithGap;
      } else {
        this.container.position.y -= this.cs.sceneWithGap;
        this.bgContainer.position.y -= this.cs.sceneWithGap;
      }
      //this.jnitAddNewScene();
    };

    this.service.fontService._loadFont('Goldman').then(() => {
      new TextElement(this, this.container, {
        x: 420,
        y: textY,
        fontFamily: 'Goldman',
        text: 'New scene',
        fontStyle: 'normal',
        fill: '#000000',
        fontSize: 42,
        fontWeight: '400',
      });
    });

    // Draw plus icon - horizontal line
    new PathElement(this, this.container, {
      position: { x: iconX, y: iconY + iconSize / 2 },
      stroke: '#000000',
      'stroke-width': 3,
      elements: [{ type: 'line', x: iconSize, y: 0 }],
    });

    // Draw plus icon - vertical line
    new PathElement(this, this.container, {
      position: { x: iconX + iconSize / 2, y: iconY },
      stroke: '#000000',
      'stroke-width': 3,
      elements: [{ type: 'line', x: 0, y: iconSize }],
    });
  }

  resetAnimation() {
    Object.values(this.shapes).forEach(shape => {
      if (shape.getType() == 'video-shape') {
        (shape as VideoShape).resetAnimation();
      }
      shape.reset();
    });
  }

  reset() {
    Object.values(this.shapes).forEach(shape => {
      shape.reset();
    });
  }

  animationDuration: number;
  calcAnimationTime() {
    const timeline = this.pseudoAnimation;
    this.animationDuration = timeline.duration();
    return this.animationDuration;
  }

  getAbsoluteStartTime() {
    let time = 0;
    let prev = this.prev;
    while (prev) {
      time += prev.animationDuration || 0;
      prev = prev.prev;
    }
    return time;
  }
  disablePositionUpdate = false;
  disablePositionUpdateForPrevs() {
    this.disablePositionUpdate = true;
    this.prev?.disablePositionUpdateForPrevs();
  }

  enablePositionUpdateForPrevs() {
    this.disablePositionUpdate = false;
    this.prev?.enablePositionUpdateForPrevs();
  }

  disablePositionUpdateForNexts() {
    this.disablePositionUpdate = true;
    this.next?.disablePositionUpdateForNexts();
  }

  enablePositionUpdateForNexts() {
    this.disablePositionUpdate = false;
    this.next?.enablePositionUpdateForNexts();
  }

  updateDefaultPosition() {
    this.defaultPosition = this.scene.positionIndex * this.cs.sceneWithGap;
  }

  /**
   * Start the local timer that counts from 0 to 1
   */

  /**
   * Reset the local timer to 0
   */
  get pseudoAnimation(): gsap.core.Timeline {
    return this.getPseudoAnimation(this.scene.animation);
  }

  getPseudoAnimation(animation: StudioAnimation) {
    if (!animation) {
      const timeline = gsap.timeline();
      timeline.add(gsap.to({}, { duration: 1 }));
      return timeline;
    }

    // -- // -- //
    const { target, next, paralell } = animation;

    const timeline =
      this.shapes[target]?.pseudoAnimation(animation) || gsap.timeline();

    if (next) {
      const nextTimeline = this.getPseudoAnimation(next);
      if (next.delay) {
        timeline.add(nextTimeline, `+=${next.delay}`);
      } else {
        timeline.add(nextTimeline, 0);
      }
    }
    if (paralell) {
      // const paralellTimeline = gsap.timeline();
      // addToTimeLine(paralellTimeline, paralell);
      // timeline.add(paralellTimeline, 0);
    }
    return timeline;
  }

  set localTime(value: number) {
    this.studioService.sceneLocalTime = value;
  }

  getTargetIRI(target: string) {
    if (target?.includes(':')) {
      const [type, index] = target.split(':');
      switch (type) {
        case 'text':
          return this.scene.texts?.[+index];
        case 'image':
          return this.scene.images?.[+index];
        case 'video':
          return this.scene.videoInstances?.[+index];
      }
    }
    return target;
  }

  gsapFunctionsAnimation({
    animation,
    functions,
  }: {
    animation: StudioAnimation;
    functions: Record<string, StudioAnimation>;
  }) {
    const { function: functionTarget } = animation;
    const fcnAnimation = functions[functionTarget];
    if (!fcnAnimation) {
      console.warn('Function not found', functionTarget);
      return gsap.timeline();
    }

    const fcnTimeline = this.gsapAnimate({
      animation: fcnAnimation,
      functions,
    });

    const { next, paralell } = animation;
    if (next) {
      const { delay, overlap } = next;
      const offset = overlap ? `-=${overlap}` : `+=${delay || 0}`;
      const nextTimeline = this.gsapAnimate({
        animation: next,
        functions,
      });
      fcnTimeline.add(nextTimeline, offset);
    }

    if (paralell) {
      const paralellTimeline = this.gsapAnimate({
        animation: paralell,
        functions,
      });
      const offset = paralell.delay || 0;
      fcnTimeline.add(paralellTimeline, paralell.delay || 0);
    }

    return fcnTimeline;
  }

  timeline: gsap.core.Timeline;
  gsapAnimate(params?: {
    animation?: StudioAnimation;
    complete?: boolean;
    functions?: Record<string, StudioAnimation>;
  }) {
    // console.log('gsapAnimate', params);
    const timeline = gsap.timeline();
    const { texts, images, videoInstances, elements } = this.scene;
    const animation =
      params?.animation ||
      this.animationByLayout ||
      this.scene.animation ||
      ({
        target: texts?.[0] || images?.[0] || videoInstances?.[0],
        duration: 1,
        type: 'appear',
        sceneTransition: {
          duration: 1,
        },
      } as StudioAnimation);

    if (videoInstances?.length) {
      // -- // -- //
      const [videoId] = videoInstances;
      const videoShape = this.shapes[videoId] as VideoShape;
      if (videoShape) {
        const videoTimeline = videoShape.gsapAnimateStep(animation);
        timeline.add(videoTimeline, 0);
      }
    }

    // console.log('gsapAnimate', animation); //
    const { complete } = params || {};

    let { functions } = params || {};
    if (!animation) {
      console.warn('Animation is not defined');
      return gsap.timeline();
    }
    const {
      target,
      sceneTransition,
      function: functionTarget,
      functions: baseFunctions,
    } = animation;

    functions ||= baseFunctions;

    if (functionTarget) {
      const fcnAnimation = functions[functionTarget];
      const fcnTimeline = this.gsapFunctionsAnimation({
        animation,
        functions,
      });
      timeline.add(fcnTimeline, 0);
    }

    if (target) {
      let targetId: string;
      if (target.includes(':')) {
        const [type, index] = target.split(':');
        switch (type) {
          case 'text':
            targetId = texts?.[+index];
            break;
          case 'image':
            targetId = images?.[+index];
            break;
          case 'video':
            targetId = videoInstances?.[+index];
            break;
          case 'element':
            targetId = elements?.[+index];
            break;
        }
      } else {
        targetId = target;
      }

      const shape = this.shapes[targetId];
      if (!shape) {
        console.warn('Shape not found', target, 'targetId', targetId);
        return timeline;
      }
      // timeline = gsap.timeline({
      //   onUpdate: () => {
      //     // Normalize timeline time based on progress and expected animation duration
      //     const progress = this.timeline.progress();
      //     this.localTime = progress * this.animationDuration;
      //   },
      // });

      const shapeTimeline = shape.gsapAnimateStep(animation);
      timeline.add(shapeTimeline, 0);
    }

    if (complete) {
      this.animateSceneTransition(sceneTransition, timeline);
    }
    return timeline;
  }

  animateSceneTransition(
    sceneTransition: SceneTransition,
    timeline: gsap.core.Timeline,
  ) {
    if (!this.next) {
      return;
    }

    console.log('animateSceneTransition', sceneTransition);
    if (!sceneTransition) {
      // -- //
      const startTimeline = gsap.to({}, { duration: 0.0001 });
      const sceneTransitionTimeline = gsap.to({}, { duration: 0 });
      //
      startTimeline.eventCallback('onStart', () => {
        console.log('sceneTransitionTimeline > on-start');

        this.store.dispatch(
          setCurrentStudioSceneId({ value: this.next.scene.id }),
        );
        this.moveNextScenes();
      });

      sceneTransitionTimeline.eventCallback('onComplete', () => {
        console.log('sceneTransitionTimeline > on-complete');
        this.movePrevScenes();
      });

      timeline.add(startTimeline);
      timeline.add(sceneTransitionTimeline);

      const next = this.next;
      if (next) {
        timeline.add(next.gsapAnimate({ complete: true }));
      }
    }
  }

  movePrevScenes() {
    let prev = this.prev;
    this.moveMe();
    while (prev) {
      if (this.cs.isPortrait) {
        prev.container.position.x -= this.cs.sceneWithGap;
        prev.bgContainer.position.x -= this.cs.sceneWithGap;
      } else {
        prev.container.position.y -= this.cs.sceneWithGap;
        prev.bgContainer.position.y -= this.cs.sceneWithGap;
      }
      prev = prev.prev;
    }
  }

  get offsetXY() {
    const offset = this.cs.currentScreenX * 0.05;
    return [offset, offset];
  }

  get sceneWidth() {
    const [xOffset] = this.offsetXY;
    return this.cs.currentScreenX - 2 * xOffset;
  }

  get sceneHeight() {
    const [_, yOffset] = this.offsetXY;
    return this.cs.currentScreenY - 2 * yOffset;
  }

  moveMe() {
    if (!this.next) {
      return;
    }
    if (this.cs.isPortrait) {
      this.cs.currentX -= this.cs.sceneWithGap;
      this.studioService.currentX -= this.cs.sceneWithGap;
      this.container.position.x -= this.cs.sceneWithGap;
      this.bgContainer.position.x -= this.cs.sceneWithGap;
    } else {
      this.cs.currentY -= this.cs.sceneWithGap;
      this.studioService.currentY -= this.cs.sceneWithGap;
      this.container.position.y -= this.cs.sceneWithGap;
      this.bgContainer.position.y -= this.cs.sceneWithGap;
    }
    console.log(
      'moveMe',
      this.cs.currentY,
      this.studioService.currentY,
      this.container.position.y,
      this.bgContainer.position.y,
    );
  }

  moveNextScenes() {
    let next = this.next;
    while (next) {
      if (this.cs.isPortrait) {
        next.container.position.x -= this.cs.sceneWithGap;
        next.bgContainer.position.x -= this.cs.sceneWithGap;
      } else {
        next.container.position.y -= this.cs.sceneWithGap;
        next.bgContainer.position.y -= this.cs.sceneWithGap;
      }
      next = next.next;
    }
  }

  bgContainer: Container;

  private initContainers() {
    // -- // -- // -- // -- // -- // -- //
    this.defaultPosition = this.scene.positionIndex * this.cs.sceneWithGap;

    this.container = new Container();
    this.bgContainer = new Container();
    this.circleContainer = new Container();
    this.container.addChild(this.circleContainer);

    //if (this.scene.id !== 'add-after-last') {
    this.initBackground(this.scene.background);

    if (this.cs.isPortrait) {
      this.container.x =
        this.defaultPosition +
        this.studioService.currentX / (this.shapeService.cs.fitScale || 1);
      this.container.y = 0;
    } else {
      this.container.x = 0;
      this.container.y =
        this.defaultPosition +
        this.studioService.currentY / (this.shapeService.cs.fitScale || 1);
    }

    this.bgContainer.position.x = this.container.position.x;
    this.bgContainer.position.y = this.container.position.y;

    if (!this.config?.preview) {
      this.cs.canvasContainer.addChild(this.container);
      this.cs.canvasBackgroundContainer.addChild(this.bgContainer);
    }
  }

  initBackground(def: StudioSceneBackgroundDefinition) {
    const bgContainer =
      this.service.backgroundService.initStudioSceneBackground(
        def,
        this.cs.isPortrait,
      );
    this.bgContainer.addChild(bgContainer);
  }

  patch(descriptor: StudioScene) {
    this.scene = descriptor;
    this.initShapes();
  }

  setPositionByCurrentY(currentY: number) {
    if (this.disablePositionUpdate) {
      return;
    }
    this.container.position.y = this.defaultPosition + currentY;
    this.bgContainer.position.y = this.defaultPosition + currentY;
  }

  setPositionByCurrentX(currentX: number) {
    // console.log('setPositionByCurrentX', currentX);
    if (this.disablePositionUpdate) {
      return;
    }
    // console.log('setPositionByCurrentX > int', currentX);
    this.container.position.x = this.defaultPosition + currentX;
    this.bgContainer.position.x = this.defaultPosition + currentX;
  }

  sortFcn(a, b) {
    const aIsText = a[0].includes('text');
    const bIsText = b[0].includes('text');

    // push text keys to the end
    if (aIsText && !bIsText) return 1;
    if (!aIsText && bIsText) return -1;

    const { w: aw, h: ah } = a[1].cell;
    const { w: bw, h: bh } = b[1].cell;
    // descending order
    return bw * bh - aw * ah;
  }

  initShapes() {
    const alreadyInitialized = new Set(Object.keys(this.shapes));

    this._shapes = [];
    const deleted: string[] = [];
    Object.entries(this.shapes || {}).forEach(([id, shape]) => {
      if (!this.scene.shapes[id]) {
        shape.remove();
        deleted.push(id);
        return;
      }
    });

    deleted.forEach(id => {
      delete this.shapes[id];
    });

    if (this.layout) {
      try {
        const layoutCells = this.layout.shapeCells;
        Object.entries(this.scene.shapes).map(([id]) => {
          if (!layoutCells[id]) {
            return;
          }
          console.log(
            'cell-setting',
            isEqual(this.scene.shapes[id].cell, layoutCells[id]),
            'layoutCells[id]',
            layoutCells[id],
            'original',
            this.scene.shapes[id].cell,
          );
          this.scene.shapes[id].cell = layoutCells[id];
        });
        console.log('this.scene.shapes', this.scene.shapes);
      } catch (error) {
        console.log('error > updating cells', error);
      }
    }
    // -- // -- // -- // -- // -- //

    const { texts, images, videoInstances, elements } = this.scene;

    (videoInstances || []).forEach(id => {
      if (this.shapes[id]) {
        (this.shapes[id] as VideoShape).update(
          this.scene.shapes[id] as StudioVideoShapeDescriptor,
        );
        return;
      }
      this.shapes[id] = new VideoShape(
        this,
        id,
        cloneDeep(this.scene.shapes[id] as StudioVideoShapeDescriptor),
      );
    });

    (elements || []).forEach(id => {
      if (this.shapes[id]) {
        (this.shapes[id] as StudioElementShape).update(
          this.scene.shapes[id] as StudioElementShapeDescriptor,
        );
        return;
      }
      this.shapes[id] = new StudioElementShape(
        this,
        id,
        cloneDeep(this.scene.shapes[id] as StudioElementShapeDescriptor),
      );
    });

    (images || []).forEach(id => {
      if (this.shapes[id]) {
        (this.shapes[id] as StudioImageShape).update(
          this.scene.shapes[id] as StudioImageShapeDescriptor,
        );
        return;
      }
      this.shapes[id] = new StudioImageShape(
        this,
        id,
        cloneDeep(this.scene.shapes[id] as StudioImageShapeDescriptor),
      );
    });

    (texts || []).forEach(id => {
      if (this.shapes[id]) {
        (this.shapes[id] as StudioTextShape).update(
          this.scene.shapes[id] as StudioTextShapeDescriptor,
        );
        return;
      }
      this.shapes[id] = new StudioTextShape(
        this,
        id,
        cloneDeep(this.scene.shapes[id] as StudioTextShapeDescriptor),
      );
    });

    this.maskRectangle?.remove();

    const [width, height] = [this.cs.currentScreenX, this.cs.currentScreenY];
    this.maskRectangle = new RectangleElement(this, this.container, {
      width,
      height,
      fill: '#000000',
      opacity: 0.001,
      r: 24,
    });

    Object.values(this.shapes).forEach(shape => {
      shape.container.mask = this.maskRectangle.element;
    });

    const loadPromise = Promise.all(
      Object.values(this.shapes).map(shape => shape.loadPromise),
    );

    loadPromise.then(() => {
      // console.log('loadPromise > resolved');
      // Object.entries(this.scene.shapes)
      //   .sort(this.sortFcn)
      //   .forEach(([id]) => {
      //     this.shapes[id].initRC();
      //   });
    });

    // this.container.mask = this.maskRectangle.element;
  }
  // -- // -- // -- // -- //
  save() {
    this.store.dispatch(
      saveScene({
        sceneId: this.sceneId,
        value: cloneDeep(this.scene),
      }),
    );
  }

  updateScene(scene: StudioScene) {
    // console.log('studio.scene.shape > updateScene', scene);
    const { positionIndex } = this.scene;
    const oldBackgroundDefinitionId = this.scene.backgroundDefinitionId;

    if (this.scene.background !== scene.background) {
      // -- //
      this.initBackground(scene.background);
    }

    if (positionIndex != scene.positionIndex) {
      this.defaultPosition = scene.positionIndex * this.cs.sceneWithGap;
      // console.log('to', this.defaultPosition + this.studioService.currentY); // -- //
      gsap.to(this.container.position, {
        y:
          this.defaultPosition +
          this.studioService.currentY / this.shapeService.cs.fitScale,
        duration: 0.5,
        ease: 'power1.inOut',
      });
      if (this.bgContainer) {
        gsap.to(this.bgContainer.position, {
          y:
            this.defaultPosition +
            this.studioService.currentY / this.shapeService.cs.fitScale,
          duration: 0.5,
          ease: 'power1.inOut',
        });
      }
    }
    this.scene.positionIndex = scene.positionIndex;

    const newScene = omit(scene, 'positionIndex');
    const oldScene = omit(this.scene, 'positionIndex');
    if (isEqual(oldScene, newScene)) {
      return;
    }

    // console.log('compareShapes', isEqual(oldScene.shapes, newScene.shapes));

    // Check if background definition changed
    const backgroundChanged =
      oldBackgroundDefinitionId !== scene.backgroundDefinitionId;

    // If background changed, recreate the background container
    if (backgroundChanged && this.bgContainer) {
      this.cs.canvasContainer.removeChild(this.bgContainer);
      this.bgContainer.destroy({ children: true });
      this.bgContainer = null;

      // Create new background container
      this.bgContainer = this.cs.backgroundService.initById(
        scene.backgroundDefinitionId,
      );
      if (this.bgContainer) {
        this.cs.canvasContainer.addChild(this.bgContainer);
        this.bgContainer.position.x = 0;
        this.bgContainer.position.y =
          this.defaultPosition +
          this.studioService.currentY / (this.shapeService.cs.fitScale || 1);
      }
    }
    if (isEqual(oldScene.shapes, newScene.shapes)) {
      // console.log('no need to update shapes');
      return;
    }
    // -- // -- // --
    this.scene = cloneDeep(scene);
    this.initShapes();
  }

  addVideo(id: string, descriptor: StudioVideoShapeDescriptor) {
    this.shapes[id] = new VideoShape(this, id, descriptor);
    this.scene.shapes[id] = descriptor;
    this.save();
  }
  removeVideo(index: number) {
    const videoId = `video:${index}`;

    if (!this.scene.shapes[videoId]) {
      console.warn('No video to remove in the scene descriptor');
      return;
    }

    const { videoInstanceId } = this.scene.shapes[
      videoId
    ] as StudioVideoShapeDescriptor;

    const instance = this.studioService.videoInstances[videoInstanceId];
    // -- //
    this.store.dispatch(
      deleteVideoInstance({
        videoInstanceId,
        videoId: instance?.mediaId,
      }),
    );
    this.shapes[videoId]?.remove();
    delete this.scene.shapes[videoId];

    this.scene.videoInstances = this.scene.videoInstances.filter(
      instanceId => instanceId !== videoInstanceId,
    );
    this.save();
  }

  addImage(id: string, descriptor: StudioImageShapeDescriptor) {
    this.shapes[id] = new StudioImageShape(this, id, descriptor);
    this.scene.shapes[id] = descriptor;
    this.save();
  }

  removeImage(index: number) {
    const imageId = `image:${index}`;

    if (!this.scene.shapes[imageId]) {
      console.warn('No image to remove in the scene descriptor');
      return;
    }

    const descriptor = this.scene.shapes[imageId] as StudioImageShapeDescriptor;

    this.shapes[imageId]?.remove();
    delete this.scene.shapes[imageId];

    this.scene.images = (this.scene.images || []).filter(
      id => id !== descriptor.imageId,
    );
    this.save();
  }

  get sceneId() {
    return this.scene.id;
  }

  get sceneIndex() {
    return this.scene.index;
  }

  get text() {
    return this.scene.texts;
  }

  get sceneLayout() {
    const { texts: text, images, videoInstances: videos } = this.scene;
    const onlyText = text && !images?.length && !videos?.length;

    if (onlyText) {
      return 'only-text';
    }
    // return this.scene.layout;
  }

  /**
   * Returns the animation context for this scene.
   * Used by animation generation services to understand scene content.
   */
  getContext(): AnimationContext {
    const { texts, images, videoInstances, positionIndex } = this.scene;

    // Determine primary element type
    let primaryElement: 'text' | 'image' | 'video' = 'text';
    if (videoInstances?.length > 0) {
      primaryElement = 'video';
    } else if (images?.length > 0) {
      primaryElement = 'image';
    }

    // Determine content density
    const totalElements =
      (texts?.length || 0) +
      (images?.length || 0) +
      (videoInstances?.length || 0);
    let contentDensity: 'sparse' | 'balanced' | 'dense' = 'balanced';
    if (totalElements <= 1) {
      contentDensity = 'sparse';
    } else if (totalElements >= 4) {
      contentDensity = 'dense';
    }

    // Build text context if texts exist
    let textContext: AnimationContext['graphics']['text'] | undefined;
    if (texts?.length > 0) {
      const textId = texts[0];
      const textShape = this.shapes[textId] as StudioTextShape;
      if (textShape) {
        const descriptor = textShape.descriptor as StudioTextShapeDescriptor;
        const textContent = descriptor.text || '';
        const words = textContent.split(/\s+/).filter(w => w.length > 0);
        const lines = textContent.split('\n').filter(l => l.length > 0);
        const longestWord = words.reduce(
          (max, word) => (word.length > max ? word.length : max),
          0,
        );

        textContext = {
          count: texts.length,
          wordCount: words.length,
          lineCount: lines.length || 1,
          hasEmphasis: false, // Could be extended to detect bold/italic
          longestWordLength: longestWord,
        };
      }
    }

    // Build image context if images exist
    let imageContext: AnimationContext['graphics']['image'] | undefined;
    if (images?.length > 0) {
      const imageId = images[0];
      const imageShape = this.shapes[imageId] as StudioImageShape;
      if (imageShape) {
        const descriptor = imageShape.descriptor as StudioImageShapeDescriptor;
        const { cell } = descriptor;
        // Calculate aspect ratio from cell dimensions
        const aspectRatio = cell?.w && cell?.h ? cell.w / cell.h : 1;

        imageContext = {
          count: images.length,
          aspectRatio,
          hasTransparency: false,
        };
      }
    }

    // Get total scenes count
    const totalScenes = (this.studioService.scenesByPositionIndex || []).filter(
      shape => shape.scene.id !== 'add-after-last',
    ).length;

    // Determine scene role based on position
    let role: 'intro' | 'body' | 'emphasis' | 'conclusion' | undefined;
    if (positionIndex === 0) {
      role = 'intro';
    } else if (positionIndex === totalScenes - 1) {
      role = 'conclusion';
    } else {
      role = 'body';
    }

    return {
      graphics: {
        text: textContext,
        image: imageContext,
        layout: {
          contentDensity,
          primaryElement,
          alignment: 'center',
        },
      },
      animation: this.scene.animation,
      scene: {
        positionIndex: positionIndex || 0,
        totalScenes,
        role,
        duration: this.animationDuration,
      },
    };
  }

  show() {
    this.container.visible = true;
    this.bgContainer.visible = true;
  }

  hide() {
    this.container.visible = false;
    this.bgContainer.visible = false;
  }

  remove() {
    console.warn('studio-scene-shape > remove');
    // Stop the local timer if running
    this.container.destroy({ children: true });
    this.bgContainer.destroy({ children: true });
  }

  pushShape(shape: any) {
    this._shapes.push(shape);
  }
}
